import { useState, useEffect, useCallback } from 'react';
import {
  Plus, Trash2, Pencil, Save, X, Loader2, GripVertical, Check, AlertTriangle,
} from 'lucide-react';
import {
  DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { api } from '@/utils/api';

/* ── Types ── */
interface Stage {
  id: string;
  name: string;
  order: number;
  color: string | null;
  probability: number;
  pipelineId: string;
  _count: { deals: number };
}

interface Pipeline {
  id: string;
  name: string;
  isDefault: boolean;
  stages: Stage[];
}

const PRESET_COLORS = [
  '#6366F1', '#8B5CF6', '#A855F7', '#D946EF', '#EC4899',
  '#F97316', '#EAB308', '#22C55E', '#14B8A6', '#3B82F6',
  '#EF4444', '#64748B',
];

/* ── Sortable Stage Row ── */
function SortableStageRow({
  stage, isEditing, editForm, onEdit, onSave, onCancel, onChange, onDelete,
}: {
  stage: Stage;
  isEditing: boolean;
  editForm: { name: string; color: string; probability: number };
  onEdit: () => void;
  onSave: () => void;
  onCancel: () => void;
  onChange: (f: Partial<typeof editForm>) => void;
  onDelete: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: stage.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 rounded-lg border px-4 py-3 ${
        isDragging ? 'border-[var(--ink-3)] bg-[var(--saffron-50)]/50' : 'border-[var(--border-primary)] bg-[var(--bg-surface)]'
      }`}
    >
      {/* Drag handle */}
      <button {...attributes} {...listeners} className="cursor-grab text-[var(--text-tertiary)] hover:text-[var(--text-secondary)] active:cursor-grabbing">
        <GripVertical size={16} />
      </button>

      {isEditing ? (
        /* ── Edit mode ── */
        <div className="flex flex-1 items-center gap-3">
          {/* Color picker */}
          <div className="relative">
            <input
              type="color"
              value={editForm.color}
              onChange={e => onChange({ color: e.target.value })}
              className="h-8 w-8 cursor-pointer rounded border-0 p-0"
            />
          </div>
          <input
            type="text"
            value={editForm.name}
            onChange={e => onChange({ name: e.target.value })}
            className="flex-1 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-input)] px-3 py-1.5 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none"
            placeholder="Stage name"
          />
          <div className="flex items-center gap-1">
            <input
              type="number"
              min={0}
              max={100}
              value={editForm.probability}
              onChange={e => onChange({ probability: Number(e.target.value) })}
              className="w-16 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-input)] px-2 py-1.5 text-center text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none"
            />
            <span className="text-xs text-[var(--text-tertiary)]">%</span>
          </div>
          <button onClick={onSave} className="rounded-lg bg-[var(--ink-0)] p-1.5 text-white hover:bg-[var(--ink-1)]">
            <Check size={14} />
          </button>
          <button onClick={onCancel} className="rounded-lg border border-[var(--border-primary)] p-1.5 text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)]">
            <X size={14} />
          </button>
        </div>
      ) : (
        /* ── View mode ── */
        <>
          <div className="h-4 w-4 rounded-full flex-shrink-0" style={{ backgroundColor: stage.color || '#6366F1' }} />
          <span className="flex-1 text-sm font-medium text-[var(--text-primary)]">{stage.name}</span>
          <span className="rounded-full bg-[var(--bg-surface-hover)] px-2 py-0.5 text-xs font-medium text-[var(--text-secondary)]">
            {stage.probability}%
          </span>
          <span className="text-xs text-[var(--text-tertiary)]">
            {stage._count.deals} deal{stage._count.deals !== 1 ? 's' : ''}
          </span>
          <button onClick={onEdit} className="rounded p-1 text-[var(--text-tertiary)] hover:bg-[var(--bg-surface-hover)] hover:text-[var(--text-primary)]">
            <Pencil size={14} />
          </button>
          <button
            onClick={onDelete}
            className="rounded p-1 text-[var(--text-tertiary)] hover:bg-red-50 hover:text-red-600"
            title={stage._count.deals > 0 ? 'Move deals first to delete' : 'Delete stage'}
          >
            <Trash2 size={14} />
          </button>
        </>
      )}
    </div>
  );
}

/* ── Main PipelinesTab ── */
export function PipelinesTab() {
  const [pipelines, setPipelines] = useState<Pipeline[]>([]);
  const [activePipelineId, setActivePipelineId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Inline editing
  const [editingStageId, setEditingStageId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({ name: '', color: '#6366F1', probability: 0 });

  // Add stage
  const [showAddStage, setShowAddStage] = useState(false);
  const [newStage, setNewStage] = useState({ name: '', color: '#6366F1', probability: 0 });

  // Pipeline name editing
  const [editingPipelineName, setEditingPipelineName] = useState(false);
  const [pipelineNameForm, setPipelineNameForm] = useState('');

  // Add pipeline modal
  const [showAddPipeline, setShowAddPipeline] = useState(false);
  const [newPipelineName, setNewPipelineName] = useState('');

  // Confirm delete
  const [confirmDelete, setConfirmDelete] = useState<{ type: 'stage' | 'pipeline'; id: string; name: string; dealCount?: number } | null>(null);

  // Toast
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);
  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor),
  );

  const activePipeline = pipelines.find(p => p.id === activePipelineId);

  const loadPipelines = useCallback(async () => {
    try {
      const res = await api.get('/deals/pipelines');
      const data: Pipeline[] = res.data.data ?? [];
      setPipelines(data);
      if (data.length > 0 && !data.find(p => p.id === activePipelineId)) {
        setActivePipelineId(data.find(p => p.isDefault)?.id || data[0].id);
      }
    } catch {
      showToast('Failed to load pipelines', 'error');
    }
    setLoading(false);
  }, [activePipelineId]);

  useEffect(() => { loadPipelines(); }, []);

  // ── Drag reorder ──
  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !activePipeline) return;

    const oldIndex = activePipeline.stages.findIndex(s => s.id === active.id);
    const newIndex = activePipeline.stages.findIndex(s => s.id === over.id);
    const reordered = arrayMove(activePipeline.stages, oldIndex, newIndex);

    // Optimistic update
    setPipelines(prev => prev.map(p =>
      p.id === activePipelineId ? { ...p, stages: reordered } : p
    ));

    try {
      await api.put(`/deals/pipelines/${activePipelineId}/stages/reorder`, {
        stageIds: reordered.map(s => s.id),
      });
    } catch {
      showToast('Failed to reorder stages', 'error');
      loadPipelines();
    }
  };

  // ── Stage CRUD ──
  const handleEditStage = (stage: Stage) => {
    setEditingStageId(stage.id);
    setEditForm({ name: stage.name, color: stage.color || '#6366F1', probability: stage.probability });
  };

  const handleSaveStage = async () => {
    if (!editingStageId || !editForm.name.trim()) return;
    setSaving(true);
    try {
      await api.put(`/deals/pipelines/${activePipelineId}/stages/${editingStageId}`, editForm);
      setEditingStageId(null);
      await loadPipelines();
      showToast('Stage updated');
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Failed to update stage', 'error');
    }
    setSaving(false);
  };

  const handleAddStage = async () => {
    if (!newStage.name.trim()) return;
    setSaving(true);
    try {
      await api.post(`/deals/pipelines/${activePipelineId}/stages`, newStage);
      setShowAddStage(false);
      setNewStage({ name: '', color: '#6366F1', probability: 0 });
      await loadPipelines();
      showToast('Stage added');
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Failed to add stage', 'error');
    }
    setSaving(false);
  };

  const handleDeleteStage = async (stageId: string) => {
    setSaving(true);
    try {
      await api.delete(`/deals/pipelines/${activePipelineId}/stages/${stageId}`);
      setConfirmDelete(null);
      await loadPipelines();
      showToast('Stage deleted');
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Failed to delete stage', 'error');
    }
    setSaving(false);
  };

  // ── Pipeline CRUD ──
  const handleSavePipelineName = async () => {
    if (!pipelineNameForm.trim()) return;
    setSaving(true);
    try {
      await api.put(`/deals/pipelines/${activePipelineId}`, { name: pipelineNameForm });
      setEditingPipelineName(false);
      await loadPipelines();
      showToast('Pipeline renamed');
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Failed to rename', 'error');
    }
    setSaving(false);
  };

  const handleAddPipeline = async () => {
    if (!newPipelineName.trim()) return;
    setSaving(true);
    try {
      const res = await api.post('/deals/pipelines', { name: newPipelineName });
      setShowAddPipeline(false);
      setNewPipelineName('');
      setActivePipelineId(res.data.data.id);
      await loadPipelines();
      showToast('Pipeline created with default stages');
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Failed to create pipeline', 'error');
    }
    setSaving(false);
  };

  const handleDeletePipeline = async () => {
    setSaving(true);
    try {
      await api.delete(`/deals/pipelines/${activePipelineId}`);
      setConfirmDelete(null);
      setActivePipelineId('');
      await loadPipelines();
      showToast('Pipeline deleted');
    } catch (err: any) {
      showToast(err.response?.data?.error || 'Failed to delete pipeline', 'error');
    }
    setSaving(false);
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" /></div>;
  }

  return (
    <div>
      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[var(--text-primary)]">Sales Pipelines</h2>
          <p className="text-sm text-[var(--text-secondary)]">Configure deal stages, probabilities and colors. Drag to reorder.</p>
        </div>
        <button
          onClick={() => { setShowAddPipeline(true); setNewPipelineName(''); }}
          className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)]"
        >
          <Plus size={14} /> Add Pipeline
        </button>
      </div>

      {/* Pipeline selector */}
      {pipelines.length > 1 && (
        <div className="mb-4 flex items-center gap-2">
          {pipelines.map(p => (
            <button
              key={p.id}
              onClick={() => { setActivePipelineId(p.id); setEditingStageId(null); setShowAddStage(false); }}
              className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors ${
                p.id === activePipelineId
                  ? 'bg-[var(--saffron-50)] text-[var(--ink-0)] dark:bg-[var(--ink-0)]/30 dark:text-[var(--saffron-600)]'
                  : 'text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)]'
              }`}
            >
              {p.name}
              {p.isDefault && <span className="ml-1 text-xs opacity-60">(default)</span>}
            </button>
          ))}
        </div>
      )}

      {activePipeline && (
        <div className="space-y-4">
          {/* Pipeline name */}
          <div className="flex items-center gap-2">
            {editingPipelineName ? (
              <>
                <input
                  type="text"
                  value={pipelineNameForm}
                  onChange={e => setPipelineNameForm(e.target.value)}
                  className="rounded-lg border border-[var(--border-primary)] bg-[var(--bg-input)] px-3 py-1.5 text-sm font-semibold text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none"
                  autoFocus
                  onKeyDown={e => { if (e.key === 'Enter') handleSavePipelineName(); if (e.key === 'Escape') setEditingPipelineName(false); }}
                />
                <button onClick={handleSavePipelineName} disabled={saving} className="rounded-lg bg-[var(--ink-0)] p-1.5 text-white hover:bg-[var(--ink-1)]">
                  <Check size={14} />
                </button>
                <button onClick={() => setEditingPipelineName(false)} className="rounded-lg border border-[var(--border-primary)] p-1.5 text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)]">
                  <X size={14} />
                </button>
              </>
            ) : (
              <>
                <h3 className="text-base font-semibold text-[var(--text-primary)]">{activePipeline.name}</h3>
                <button
                  onClick={() => { setEditingPipelineName(true); setPipelineNameForm(activePipeline.name); }}
                  className="rounded p-1 text-[var(--text-tertiary)] hover:bg-[var(--bg-surface-hover)] hover:text-[var(--text-primary)]"
                >
                  <Pencil size={14} />
                </button>
              </>
            )}
          </div>

          {/* Stages list */}
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext items={activePipeline.stages.map(s => s.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {activePipeline.stages.map(stage => (
                  <SortableStageRow
                    key={stage.id}
                    stage={stage}
                    isEditing={editingStageId === stage.id}
                    editForm={editForm}
                    onEdit={() => handleEditStage(stage)}
                    onSave={handleSaveStage}
                    onCancel={() => setEditingStageId(null)}
                    onChange={f => setEditForm(prev => ({ ...prev, ...f }))}
                    onDelete={() => {
                      if (stage._count.deals > 0) {
                        setConfirmDelete({ type: 'stage', id: stage.id, name: stage.name, dealCount: stage._count.deals });
                      } else {
                        setConfirmDelete({ type: 'stage', id: stage.id, name: stage.name, dealCount: 0 });
                      }
                    }}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          {/* Add stage inline form */}
          {showAddStage ? (
            <div className="flex items-center gap-3 rounded-lg border border-dashed border-[var(--ink-3)] bg-[var(--saffron-50)]/30 px-4 py-3 dark:bg-[var(--ink-0)]/10">
              <input
                type="color"
                value={newStage.color}
                onChange={e => setNewStage(s => ({ ...s, color: e.target.value }))}
                className="h-8 w-8 cursor-pointer rounded border-0 p-0"
              />
              <input
                type="text"
                value={newStage.name}
                onChange={e => setNewStage(s => ({ ...s, name: e.target.value }))}
                className="flex-1 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-input)] px-3 py-1.5 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none"
                placeholder="Stage name"
                autoFocus
                onKeyDown={e => { if (e.key === 'Enter') handleAddStage(); if (e.key === 'Escape') setShowAddStage(false); }}
              />
              <div className="flex items-center gap-1">
                <input
                  type="number"
                  min={0}
                  max={100}
                  value={newStage.probability}
                  onChange={e => setNewStage(s => ({ ...s, probability: Number(e.target.value) }))}
                  className="w-16 rounded-lg border border-[var(--border-primary)] bg-[var(--bg-input)] px-2 py-1.5 text-center text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none"
                />
                <span className="text-xs text-[var(--text-tertiary)]">%</span>
              </div>
              <button onClick={handleAddStage} disabled={saving || !newStage.name.trim()} className="rounded-lg bg-[var(--ink-0)] px-3 py-1.5 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50">
                {saving ? <Loader2 size={14} className="animate-spin" /> : 'Add'}
              </button>
              <button onClick={() => setShowAddStage(false)} className="rounded-lg border border-[var(--border-primary)] px-3 py-1.5 text-sm text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)]">
                Cancel
              </button>
            </div>
          ) : (
            <button
              onClick={() => setShowAddStage(true)}
              className="flex w-full items-center justify-center gap-2 rounded-lg border border-dashed border-[var(--border-primary)] py-3 text-sm font-medium text-[var(--text-secondary)] hover:border-[var(--ink-3)] hover:text-[var(--ink-0)] transition-colors"
            >
              <Plus size={16} /> Add Stage
            </button>
          )}

          {/* Color presets hint */}
          <div className="mt-2">
            <p className="mb-2 text-xs font-medium text-[var(--text-tertiary)]">Quick colors:</p>
            <div className="flex flex-wrap gap-1.5">
              {PRESET_COLORS.map(c => (
                <button
                  key={c}
                  onClick={() => {
                    if (editingStageId) setEditForm(prev => ({ ...prev, color: c }));
                    else if (showAddStage) setNewStage(prev => ({ ...prev, color: c }));
                  }}
                  className="h-5 w-5 rounded-full border border-[var(--border-primary)] transition-transform hover:scale-125"
                  style={{ backgroundColor: c }}
                  title={c}
                />
              ))}
            </div>
          </div>

          {/* Danger zone — delete pipeline */}
          {pipelines.length > 1 && (
            <div className="mt-6 rounded-lg border border-red-200 bg-red-50/50 p-4 dark:border-red-900/30 dark:bg-red-900/10">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-red-700 dark:text-red-400">Delete Pipeline</p>
                  <p className="text-xs text-red-600/70 dark:text-red-400/60">
                    This will permanently delete "{activePipeline.name}" and all its stages. Deals must be moved first.
                  </p>
                </div>
                <button
                  onClick={() => setConfirmDelete({ type: 'pipeline', id: activePipelineId, name: activePipeline.name })}
                  className="rounded-lg border border-red-300 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-100 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-900/30"
                >
                  Delete Pipeline
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Add Pipeline Modal ── */}
      {showAddPipeline && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setShowAddPipeline(false)}>
          <div className="mx-4 w-full max-w-md rounded-xl bg-[var(--bg-card)] shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-[var(--border-primary)] px-6 py-4">
              <h2 className="text-lg font-semibold text-[var(--text-primary)]">New Pipeline</h2>
              <button onClick={() => setShowAddPipeline(false)} className="text-[var(--text-tertiary)] hover:text-[var(--text-primary)]"><X size={20} /></button>
            </div>
            <div className="px-6 py-4">
              <label className="mb-1 block text-sm font-medium text-[var(--text-secondary)]">Pipeline Name *</label>
              <input
                type="text"
                value={newPipelineName}
                onChange={e => setNewPipelineName(e.target.value)}
                className="w-full rounded-lg border border-[var(--border-primary)] bg-[var(--bg-input)] px-3 py-2 text-sm text-[var(--text-primary)] focus:border-[var(--ink-0)] focus:outline-none"
                placeholder="e.g. Enterprise Pipeline"
                autoFocus
                onKeyDown={e => { if (e.key === 'Enter') handleAddPipeline(); }}
              />
              <p className="mt-2 text-xs text-[var(--text-tertiary)]">
                Default stages (New Lead, Contacted, Qualified, Proposal Sent, Negotiation, Won, Lost) will be created automatically. You can customize them after.
              </p>
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-[var(--border-primary)] px-6 py-4">
              <button onClick={() => setShowAddPipeline(false)} className="rounded-lg border border-[var(--border-primary)] px-4 py-2 text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)]">
                Cancel
              </button>
              <button
                onClick={handleAddPipeline}
                disabled={saving || !newPipelineName.trim()}
                className="flex items-center gap-2 rounded-lg bg-[var(--ink-0)] px-4 py-2 text-sm font-medium text-white hover:bg-[var(--ink-1)] disabled:opacity-50"
              >
                {saving && <Loader2 size={14} className="animate-spin" />}
                Create Pipeline
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirm Delete Modal ── */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={() => setConfirmDelete(null)}>
          <div className="mx-4 w-full max-w-md rounded-xl bg-[var(--bg-card)] shadow-xl" onClick={e => e.stopPropagation()}>
            <div className="px-6 py-5">
              <div className="mb-3 flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/30">
                  <AlertTriangle size={20} className="text-red-600 dark:text-red-400" />
                </div>
                <h2 className="text-lg font-semibold text-[var(--text-primary)]">
                  Delete {confirmDelete.type === 'pipeline' ? 'Pipeline' : 'Stage'}
                </h2>
              </div>
              {confirmDelete.dealCount && confirmDelete.dealCount > 0 ? (
                <p className="text-sm text-[var(--text-secondary)]">
                  <strong>"{confirmDelete.name}"</strong> has <strong>{confirmDelete.dealCount} deal{confirmDelete.dealCount !== 1 ? 's' : ''}</strong>.
                  Move all deals to another stage before deleting.
                </p>
              ) : (
                <p className="text-sm text-[var(--text-secondary)]">
                  Are you sure you want to delete <strong>"{confirmDelete.name}"</strong>? This cannot be undone.
                </p>
              )}
            </div>
            <div className="flex items-center justify-end gap-3 border-t border-[var(--border-primary)] px-6 py-4">
              <button onClick={() => setConfirmDelete(null)} className="rounded-lg border border-[var(--border-primary)] px-4 py-2 text-sm font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-surface-hover)]">
                Cancel
              </button>
              {(!confirmDelete.dealCount || confirmDelete.dealCount === 0) && (
                <button
                  onClick={() => confirmDelete.type === 'pipeline' ? handleDeletePipeline() : handleDeleteStage(confirmDelete.id)}
                  disabled={saving}
                  className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {saving && <Loader2 size={14} className="animate-spin" />}
                  Delete
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* ── Toast ── */}
      {toast && (
        <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-2 rounded-lg px-4 py-3 text-white shadow-lg ${
          toast.type === 'success' ? 'bg-green-600' : 'bg-red-600'
        }`}>
          {toast.type === 'success' ? <Check size={16} /> : <AlertTriangle size={16} />}
          <span className="text-sm font-medium">{toast.msg}</span>
        </div>
      )}
    </div>
  );
}
